import { makeAutoObservable } from "mobx"
import { nanoid } from "nanoid"
import groupBy from "lodash/groupBy"

import { EventSourceMessage } from "@microsoft/fetch-event-source"
import searchService, {
  StreamMessage,
  AnswerSnapshotEvent,
} from "@services/search.service"
import {
  AnswerViewType,
  QuestionFeedbackType,
} from "@framework/constants/search-results"
import RestrictionsStore from "@store/restrictions/restrictions.store"
import { AnswerItem } from "@framework/types/search"
import { AvatarData } from "@framework/types/avatar"
import { QueryFilterData } from "@framework/types/query-filter"
import { QueryHistoryData } from "@framework/types/analytics"
import knowledgeService from "@services/knowledge.service"
import { errorToText } from "@store/utils/error-handlers"
import contentManagerService from "@services/content-manager.service"
import { Company } from "@framework/types/company"
import { SearchMode } from "@framework/types/search-mode"
import WorkbookReportsStore from "@store/unified-matrix/workbook-reports.store"

import SearchSummaryBlockStore from "../search-summary-block.store"
import FilterEntityStore from "../filter-entity.store"
import SearchAnswersDataStore from "../search-answers-data.store"
import SearchPassagesStore from "../search-passages.store"
import { AnswerSource, extractSources } from "../dataTransformers"
import AnswerFeedbackStore from "../answer-feedback.store"
import SearchMessageBlockStore from "../search-message-block.store"
import SearchFlowStore, { SearchEntity } from "../search-flow.store"
import SearchEntityStore from "../search-entity.store"
import SearchSourcesStore from "../search-sources.store"
import ISearchEntityBlockStore from "../types"
import FactFinderSolutionStore from "./fact-finder-solution.store"
import SearchCitationsStore, {
  ContentCitationData,
  ExpertInsightCitationData,
} from "../search-citations.store"
import WorkbookEntityStore, {
  WorkbookReportRequest,
} from "../workbook-entity.store"
import chatService from "@services/chat.service"
import QueryAttachment from "@store/search/query-attachment"
import QueryAttachmentsStore from "@store/search/query-attachments.store"
import SearchAnswersContextStore from "../search-answers-context.store"

export class FactFinderSolutionController {
  // injections

  restrictionsStore: RestrictionsStore

  factFinderSolutionStore = new FactFinderSolutionStore()

  workbookReportsStore = new WorkbookReportsStore()

  private searchAbortController: AbortController | null = null

  get searchFlow(): SearchFlowStore {
    return this.factFinderSolutionStore.searchFlowStore
  }

  get queryAttachments(): QueryAttachmentsStore {
    return this.factFinderSolutionStore.queryAttachments
  }

  // state

  // constructor

  constructor(injections: {
    restrictionsStore: RestrictionsStore
    factFinderSolutionStore: FactFinderSolutionStore
    workbookReportsStore: WorkbookReportsStore
  }) {
    this.restrictionsStore = injections.restrictionsStore
    this.factFinderSolutionStore = injections.factFinderSolutionStore
    this.workbookReportsStore = injections.workbookReportsStore

    makeAutoObservable(this)
  }

  // actions

  reset = () => {
    this.searchFlow.reset()
    this.clearHighLights()
    this.queryAttachments.reset()
  }

  resetChat = () => {
    this.reset()
  }

  clearHighLights = () => {
    this.factFinderSolutionStore.highlights = null
  }

  private buildSearchTools = (attachmentSessionId?: string) => {
    const { searchFlowStore } = this.factFinderSolutionStore
    let { searchMode } = searchFlowStore

    if (attachmentSessionId && searchMode === SearchMode.ONLINE) {
      searchMode = SearchMode.QUICK
    }

    if (searchMode === SearchMode.ONLINE) {
      return { searchMode: SearchMode.ONLINE }
    }

    return {
      searchMode,
      answerFormat: searchFlowStore.answerFormat,
      withWebResearch: this.restrictionsStore.allowWebSearch
        ? searchFlowStore.isWebSearchEnabled
        : undefined,
    }
  }

  searchMessageHandler =
    (store: SearchEntityStore) => (response: EventSourceMessage) => {
      const message = JSON.parse(response.data) as StreamMessage

        const blockId = message.meta?.queryType ?? message.meta?.id ?? nanoid()

        store.pushMessage(message)

        if (message.type === "ALL_CITATION_CONTEXTS") {
          if (!store.hasBlock(blockId)) {
            store.addBlock(new SearchSummaryBlockStore({ id: blockId }))
          }

          const blockStore = store.getBlockById(blockId)

          if (!(blockStore instanceof SearchSummaryBlockStore))
            throw new Error("Wrong Store type")

          blockStore.citationsStore?.searchAnswersContextStore.addCitationContext(
            message.data.citationContexts ?? []
          )
        }

        if (message.type === "SEARCH_RESULT") {
          if (!store.hasBlock(blockId)) {
            store.addBlock(new SearchSummaryBlockStore({ id: blockId }))
          }

          const blockStore = store.getBlockById(blockId)

          if (!(blockStore instanceof SearchSummaryBlockStore))
            throw new Error("Wrong Store type")

          const answersDataStore = new SearchAnswersDataStore({
            rawAnswer: message.data.searchResults,
          })

          const answersContextStore = new SearchAnswersContextStore()

          const passagesStore = new SearchPassagesStore({
            restrictionsStore: this.restrictionsStore,
            searchAnswers: answersDataStore,
            filter: store.filter,
          })

          const sourcesList = extractSources(message.data.searchResults)

          const sourcesStore = new SearchSourcesStore({
            sourcesList,
            restrictionsStore: this.restrictionsStore,
            searchPassages: passagesStore,
            filter: store.filter,
          })

          const citationsStore = new SearchCitationsStore({
            searchAnswersStore: answersDataStore,
            searchAnswersContextStore: answersContextStore,
          })

          blockStore.queryType = message.meta?.queryType ?? "DEFAULT"
          blockStore.setAnswersDataStore(answersDataStore)
          blockStore.setPassagesStore(passagesStore)
          blockStore.setSourcesStore(sourcesStore)
          blockStore.setCitationsStore(citationsStore)

          this.loadCitationsDetails(citationsStore)
          this.updateSources(sourcesStore)(sourcesList)
        }

        if (message.type === "SUMMARY") {
          const summary = message.data?.summary ?? message.data

          if (!summary) {
            return
          }

          const summaryText = summary.summary ?? summary.text ?? ""

          const blockId =
            message.meta?.queryType ?? message.meta?.id ?? nanoid()
          if (!store.hasBlock(blockId)) {
            store.addBlock(new SearchSummaryBlockStore({ id: blockId }))
          }
          const blockStore = store.getBlockById(blockId)

          if (!(blockStore instanceof SearchSummaryBlockStore))
            throw new Error("Wrong Store type")

          blockStore.queryType = message.meta?.queryType ?? "DEFAULT"
          blockStore.searchSummary.summary = summaryText
          blockStore.searchSummary.highlights = summary.keyInfoList ?? []
          blockStore.searchSummary.confidence = summary.confidence ?? 0
          blockStore.searchSummary.showAttachmentTruncationWarning =
            summary.attachmentTruncated === true
          if (message?.data?.summary?.questionId)
            this.searchFlow.setQuestionId(message?.data?.summary?.questionId)
        }

        if (message.type === "SUMMARY_DELTA") {
          const summaryText = message.data.text ?? ""

          const blockId =
            message.meta?.queryType ?? message.meta?.id ?? nanoid()

          if (!store.hasBlock(blockId)) {
            const blockStore = store.addBlock(
              new SearchSummaryBlockStore({ id: blockId })
            )

            blockStore.queryType = message.meta?.queryType ?? "DEFAULT"
          }

          const blockStore = store.getBlockById(blockId)

          if (!(blockStore instanceof SearchSummaryBlockStore))
            throw new Error("Wrong Store type")

          blockStore.searchSummary.summary =
            (blockStore.searchSummary.summary || "") + summaryText
        }

        if (message.type === "WORKBOOK_REPORT_MESSAGE") {
          const blockId =
            message.meta?.queryType ?? message.meta?.id ?? nanoid()

          if (!store.hasBlock(blockId)) {
            const blockStore = store.addBlock(
              new SearchSummaryBlockStore({ id: blockId })
            )

            blockStore.queryType = message.meta?.queryType ?? "DEFAULT"
          }

          const blockStore = store.getBlockById(blockId)

          if (!(blockStore instanceof SearchSummaryBlockStore))
            throw new Error("Wrong Store type")

          const { reportData } = message.data

          const report = this.workbookReportsStore.getById(reportData.id)
          report.update(reportData)
          blockStore.setReport(reportData.id)
        }

        if (message.type === "STATUS_MESSAGE") {
          if (!store.hasBlock(blockId)) {
            if (message.meta?.queryType === blockId)
              store.addBlock(
                new SearchSummaryBlockStore({
                  id: blockId,
                })
              )
            else
              store.addBlock(
                new SearchMessageBlockStore({
                  id: blockId,
                })
              )
          }
          const blockStore = store.getBlockById(blockId)

          blockStore.addMessage(message.message)
          blockStore.queryType = message.meta?.queryType ?? "DEFAULT"

          if (message?.meta?.threadId) {
            this.searchFlow.setOldThreadId(message?.meta?.threadId)
          }
        }

        if (message.type === "ERROR") {
          const errorMessage = message.message

          if (errorMessage === "SESSION_NOT_FOUND") {
            throw new Error("SESSION_NOT_FOUND")
          }

          const status = errorMessage?.toLowerCase().includes("rate limit")
            ? "RATE_LIMIT"
            : errorMessage?.toLowerCase().includes("online search")
              ? "ONLINE_SEARCH_UNAVAILABLE"
              : "UNEXPECTED_ERROR"

          store.setError(errorMessage || "Unexpected error", status)
        }

        if (message.type === "SUGGESTED_QUESTIONS") {
          const { suggested_questions } = message.data

          const blockId =
            message.meta?.queryType ?? message.meta?.id ?? nanoid()

          let blockStore: SearchSummaryBlockStore | undefined

          if (store.hasBlock(blockId)) {
            const block = store.getBlockById(blockId)
            if (block instanceof SearchSummaryBlockStore) {
              blockStore = block
            }
          }

          if (!blockStore) {
            blockStore = store.blocks.find(
              (block) => block instanceof SearchSummaryBlockStore
            ) as SearchSummaryBlockStore | undefined
          }

          if (blockStore) {
            blockStore.searchSummary.suggestedQuestion =
              suggested_questions ?? []
          }
        }
    }

  searchUnstructured = async (searchInstance: SearchEntityStore) => {
    this.searchAbortController?.abort()
    this.searchAbortController = new AbortController()
    const { signal } = this.searchAbortController

    const runSearch = async () => {
      let sessionNotFound = false
      const { searchFlowStore } = this.factFinderSolutionStore

      await searchService.getUnstructuredSearchStream(
        searchInstance.filter.toJson(),
        this.buildSearchTools(searchInstance.filter.attachmentSessionId),
        {
          onmessage: (response) => {
            try {
              this.searchMessageHandler(searchInstance)(response)
            } catch (error: any) {
              if (error?.message === "SESSION_NOT_FOUND") {
                sessionNotFound = true
                return
              }

              throw error
            }
          },
          onerror: (err) => {
            if (signal.aborted) return
            searchInstance.setError("Failed to initiate connection")
            searchInstance.setLoading(false)
            throw err
          },
          onclose: () => {
            if (!signal.aborted) {
              searchInstance.setLoading(false)
            }
          },
        },
        signal
      )

      if (sessionNotFound) {
        throw new Error("SESSION_NOT_FOUND")
      }
    }

    try {
      searchInstance.setLoading(true)
      searchInstance.setError(null)

      try {
        await runSearch()
      } catch (error: any) {
        if (
          error?.message === "SESSION_NOT_FOUND" &&
          searchInstance.filter.attachmentSessionId
        ) {
          await this.reuploadAttachments()
          searchInstance.filter.setAttachmentSessionId(
            this.getAttachmentSessionId()
          )
          await runSearch()
          return
        }

        throw error
      }
    } catch (error: any) {
      if (signal.aborted || error?.name === "AbortError") {
        return
      }

      if (error?.message === "SESSION_NOT_FOUND") {
        searchInstance.setError(
          "Attached file session expired. Please re-attach your file and try again.",
          "UNEXPECTED_ERROR"
        )
        return
      }

      const message =
        error?.message || "Could not complete search. Please try again."
      searchInstance.setError(message, "UNEXPECTED_ERROR")
      console.error(error)
    } finally {
      searchInstance.setLoading(false)
      if (this.searchAbortController?.signal === signal) {
        this.searchAbortController = null
      }
    }
  }

  cancelSearch = () => {
    this.searchAbortController?.abort()
    this.searchAbortController = null

    for (let i = this.searchFlow.flowSequence.length - 1; i >= 0; i -= 1) {
      const entity = this.searchFlow.getById(this.searchFlow.flowSequence[i])
      if (entity instanceof SearchEntityStore && entity.isLoading) {
        entity.setLoading(false)
        entity.setError("Search stopped", "CANCELLED")
        break
      }
    }
  }

  private getAttachmentSessionId = (): string | undefined => {
    if (!this.queryAttachments.hasAttachments) {
      return undefined
    }

    if (!this.queryAttachments.isLoaded) {
      throw new Error("ATTACHMENT_NOT_READY")
    }

    return this.queryAttachments.sessionId
  }

  createAttachment = async (
    attachment: QueryAttachment,
    sessionId?: string
  ) => {
    this.queryAttachments.addAttachment(attachment)

    try {
      await this.uploadAttachment(attachment, sessionId)
    } catch (err: any) {
      this.queryAttachments.removeAttachment(attachment)
      throw err
    }
  }

  private uploadAttachment = async (
    attachment: QueryAttachment,
    sessionId?: string
  ) => {
    attachment.setLoading(true)

    try {
      const response = await chatService.createAttachment({
        attachment,
        sessionId,
      })

      attachment.setId(response.id)
      attachment.setSessionId(response.sessionId)
    } finally {
      attachment.setLoading(false)
    }
  }

  private reuploadAttachments = async () => {
    const sessionId = nanoid()

    await Promise.all(
      this.queryAttachments.attachments.map((attachment) =>
        this.uploadAttachment(attachment, sessionId)
      )
    )
  }

  createAttachments = async (attachments: QueryAttachment[]) => {
    const sessionId = nanoid()

    return Promise.all(
      attachments.map((attachment) => {
        return this.createAttachment(attachment, sessionId)
      })
    )
  }

  deleteAttachment = async (attachment: QueryAttachment) => {
    const { id, sessionId } = attachment
    this.queryAttachments.removeAttachment(attachment)

    if (!sessionId) {
      return
    }

    try {
      await chatService.deleteAttachment({ id, sessionId })
    } catch (error) {
      console.warn("Failed to delete attachment session", error)
    }
  }

  pushReport = (request: WorkbookReportRequest) => {
    try {
      const searchInstance = new WorkbookEntityStore({
        id: nanoid(),
        request,
      })

      searchInstance.setLoading(true)

      this.searchFlow.pushSearchInstance(searchInstance)

      return searchInstance
    } catch (error) {
      // TODO: Add support for error handling
      console.log(error)
    }

    return null
  }

  search = async (
    query: string,
    avatar: AvatarData,
    solutionId: string,
    appliedFilters: QueryFilterData[],
    allCompanies: Company[]
  ): Promise<boolean> => {
    try {
      if (this.searchFlow.isAnyLoading()) {
        return false
      }

      const appliedFiltersGroup = groupBy(appliedFilters, "type")
      const combinedCompanyIds = Array.from(
        new Set([
          ...appliedFilters.map((filter) => filter.companyId).filter(Boolean),
          ...(appliedFiltersGroup?.company?.map((item) => item.id) || []),
        ])
      ) as string[]

      const publicCompanyIds = allCompanies
        .filter((company) => combinedCompanyIds.includes(company?.id))
        .flatMap((company) => company.linkedPublicCompanies || [])
        .map((linkedCompany) => linkedCompany?.id)
        .filter((id): id is string => id !== undefined)

      const attachmentSessionId = this.queryAttachments.hasAttachments
        ? this.getAttachmentSessionId()
        : undefined

      const attachmentFileNames = this.queryAttachments.hasAttachments
        ? this.queryAttachments.attachments.map((attachment) => attachment.file.name)
        : undefined

      const filter = new FilterEntityStore({
        query,
        avatarName: avatar.name,
        productSolutionId: solutionId,
        avatarId: avatar.id,
        oldThreadId: this.searchFlow.oldThreadId,
        dataTypes: appliedFiltersGroup?.dataType?.map((item) => item.name),
        dataSources: appliedFiltersGroup?.dataSource?.map((item) => item.name),
        categoryIds: appliedFiltersGroup?.categories?.map((item) => item.id),
        productIds: appliedFiltersGroup?.products?.map((item) => item.id),
        publicProductIds: appliedFiltersGroup?.products?.reduce<string[]>(
          (acc, item) => {
            if (item.publicProductId) acc.push(item.publicProductId)
            return acc
          },
          []
        ),
        companyIds: combinedCompanyIds,
        publicCompanyIds,
        attachmentSessionId,
        attachmentFileNames,
      })

      const searchInstance = new SearchEntityStore({
        id: nanoid(),
        filter,
      })
      // Mark loading before returning so submit UI updates immediately
      searchInstance.setLoading(true)
      this.searchFlow.pushSearchInstance(searchInstance)

      // Do not await the full SSE stream — submit should resolve once search has started.
      // Keep attachments until the stream finishes so SESSION_NOT_FOUND can re-upload,
      // and so the file chip stays visible while Thinking.
      this.searchUnstructured(searchInstance).finally(() => {
        if (attachmentSessionId) {
          this.queryAttachments.reset()
        }
      })

      return true
    } catch (error: any) {
      // TODO: Add support for error handling
      if (error?.message === "ATTACHMENT_NOT_READY") {
        throw error
      }
      console.log(error)
      return false
    }
  }

  restoreRecentQuestion = async (
    historyItem: QueryHistoryData,
    avatar: AvatarData,
    solutionId: string,
    appliedFilters: QueryFilterData[],
    allCompanies: Company[]
  ) => {
    try {
      if (this.searchFlow.isAnyLoading()) {
        return
      }

      const appliedFiltersGroup = groupBy(appliedFilters, "type")
      const combinedCompanyIds = Array.from(
        new Set([
          ...appliedFilters.map((filter) => filter.companyId).filter(Boolean),
          ...(appliedFiltersGroup?.company?.map((item) => item.id) || []),
        ])
      ) as string[]

      const publicCompanyIds = allCompanies
        .filter((company) => combinedCompanyIds.includes(company?.id))
        .flatMap((company) => company.linkedPublicCompanies || [])
        .map((linkedCompany) => linkedCompany?.id)
        .filter((id): id is string => id !== undefined)

      const filter = new FilterEntityStore({
        query: historyItem.question,
        avatarName: avatar.name,
        productSolutionId: solutionId,
        avatarId: avatar.id,
        oldThreadId: undefined,
        dataTypes: appliedFiltersGroup?.dataType?.map((item) => item.name),
        dataSources: appliedFiltersGroup?.dataSource?.map((item) => item.name),
        categoryIds: appliedFiltersGroup?.categories?.map((item) => item.id),
        productIds: appliedFiltersGroup?.products?.map((item) => item.id),
        publicProductIds: appliedFiltersGroup?.products?.reduce<string[]>(
          (acc, item) => {
            if (item.publicProductId) acc.push(item.publicProductId)
            return acc
          },
          []
        ),
        companyIds: combinedCompanyIds,
        publicCompanyIds,
        attachmentSessionId: this.getAttachmentSessionId(),
      })

      const searchInstance = new SearchEntityStore({
        id: nanoid(),
        filter,
      })
      this.searchFlow.pushSearchInstance(searchInstance)
      searchInstance.setLoading(true)
      searchInstance.setError(null)

      let snapshotEvents: AnswerSnapshotEvent[] | null = null
      try {
        if (historyItem.hasAnswerSnapshot || historyItem.id) {
          const response = await searchService.getQuestionSnapshot(historyItem.id)
          const snapshot = response.data?.data
          if (snapshot?.events?.length) {
            snapshotEvents = snapshot.events
          }
        }
      } catch (error) {
        console.warn("Failed to load answer snapshot; falling back", error)
      }

      if (snapshotEvents?.length) {
        this.replaySnapshotEvents(searchInstance, snapshotEvents)
        this.searchFlow.setQuestionId(historyItem.id)
        searchInstance.setLoading(false)
        return
      }

      if (historyItem.summary || historyItem.citations) {
        this.hydrateFromSummaryAndCitations(searchInstance, historyItem)
        this.searchFlow.setQuestionId(historyItem.id)
        searchInstance.setLoading(false)
        return
      }

      searchInstance.setLoading(false)
      await this.searchUnstructured(searchInstance)
    } catch (error: any) {
      console.log(error)
    }
  }

  private replaySnapshotEvents = (
    store: SearchEntityStore,
    events: AnswerSnapshotEvent[]
  ) => {
    const handler = this.searchMessageHandler(store)
    events.forEach((event) => {
      handler({
        id: "",
        event: event.type,
        data: JSON.stringify({
          type: event.type,
          message: event.message,
          data: event.data,
          meta: event.meta,
        }),
      } as EventSourceMessage)
    })
  }

  private hydrateFromSummaryAndCitations = (
    store: SearchEntityStore,
    historyItem: QueryHistoryData
  ) => {
    const blockId = nanoid()
    const handler = this.searchMessageHandler(store)

    let citationsPayload: any = { answers: [] }
    if (historyItem.citations) {
      try {
        const parsed = JSON.parse(historyItem.citations)
        citationsPayload = Array.isArray(parsed)
          ? { answers: parsed }
          : parsed?.answers
            ? parsed
            : { answers: parsed }
      } catch (error) {
        citationsPayload = { answers: [] }
      }
    }

    handler({
      id: "",
      event: "SEARCH_RESULT",
      data: JSON.stringify({
        type: "SEARCH_RESULT",
        message: null,
        data: {
          searchResults: {
            answer: citationsPayload,
            filesInfo: [],
            filesWithHit: {},
          },
        },
        meta: { id: blockId, queryType: "ADVANCED" },
      }),
    } as EventSourceMessage)

    if (historyItem.summary) {
      handler({
        id: "",
        event: "SUMMARY",
        data: JSON.stringify({
          type: "SUMMARY",
          message: null,
          data: {
            summary: {
              text: historyItem.summary,
              questionId: historyItem.id,
            },
          },
          meta: { id: blockId, queryType: "ADVANCED" },
        }),
      } as EventSourceMessage)
    }
  }

  retrySearch = async (searchId: string) => {
    try {
      const prevInstance = this.searchFlow.getById(searchId)

      if (prevInstance instanceof SearchEntityStore) {
        const searchInstance = new SearchEntityStore({
          id: prevInstance.id,
          filter: prevInstance.filter,
          retryAttempt: prevInstance.retryAttempt + 1,
        })

        this.searchFlow.pushSearchInstance(searchInstance)
        await this.searchUnstructured(searchInstance)
      }

      // ...add retry handler for other instance types
    } catch (error: any) {
      console.log(error)
    }
  }

  loadCitationsDetails = (store: SearchCitationsStore) => {
    store.citations.forEach(async (it) => {
      const error = await (it.isExpertAnswer
        ? this.loadExpertAnswerCitationDetails(store, it)
        : this.loadDocumentCitationDetails(store, it))

      // eslint-disable-next-line no-param-reassign
      if (error) it.detailsLoadingError = error
    })
  }

  loadDocumentCitationDetails = async (
    store: SearchCitationsStore,
    citation: ContentCitationData
  ) => {
    try {
      const { connectorName, contentId, sourceType } = citation

      if (!connectorName) throw new Error("Missing connectorName for citation")
      if (!contentId) throw new Error("Missing contentId for citation")

      const response = (
        await contentManagerService.getDocumentDetails({
          connectorName,
          contentId,
          isPublic: sourceType?.toLowerCase() === "public",
        })
      ).data

      const prevState = store.getById(citation.id)

      if (prevState == null)
        throw Error(`Can't find citation by passed Id: ${citation.id}`)

      if (prevState?.isExpertAnswer) throw Error("Incompatible Citation type")

      prevState.contentType = response.contentType || null
      prevState.connectedBy = response.connectedBy || null
      prevState.connectorName = response.connectorName || connectorName
      prevState.contentSourceName = response.source || null
    } catch (error: any) {
      return errorToText(error)
    }

    return null
  }

  loadExpertAnswerCitationDetails = async (
    store: SearchCitationsStore,
    citation: ExpertInsightCitationData
  ) => {
    try {
      const { questionId } = citation

      if (!questionId) throw new Error("Missing questionId for citation")

      const response = (
        await knowledgeService.getExpertQuestionByElasticSearchId(questionId)
      ).data.data

      const prevState = store.getById(citation.id)

      if (prevState == null)
        throw Error(`Can't find citation by passed Id: ${citation.id}`)

      if (!prevState.isExpertAnswer) throw Error("Incompatible Citation type")

      prevState.question = response.question
      prevState.verifiedBy = response.verifiedBy
    } catch (error: any) {
      return errorToText(error)
    }
    return null
  }

  /**
   * @deprecated metadata used to be used to show file size for passages
   */
  updateSources =
    (store: SearchSourcesStore) => async (sources: AnswerSource[]) => {
      try {
        const requiresUpdateIds = sources
          .filter(({ fileSize }) => fileSize == null)
          .map(({ id: source }) => ({
            source,
          }))
        if (requiresUpdateIds.length === 0) return

        const response = await searchService.getFilesInfo(requiresUpdateIds)
        const info = response.data.data
        if (!Array.isArray(info)) throw new Error("Unexpected response format")

        store.applySourcesMetaData(info)
      } catch (error) {
        // ignore errors
      }
    }

  voteForSearchSummary = async (
    blockStore: SearchEntityStore,
    question: string,
    answerData: string,
    reason: string,
    summaryType?: string,
    productSolutionId?: string,
    rating: number = 3
  ) => {
    const store = new AnswerFeedbackStore({ rating, reason })
    try {
      store.isLoading = true
      store.data = answerData

      const feedback =
        rating > 3
          ? QuestionFeedbackType.POSITIVE
          : rating === 3
          ? QuestionFeedbackType.NEUTRAL
          : QuestionFeedbackType.NEGATIVE

      await searchService.sendAnswerFeedback({
        question,
        answer: JSON.stringify(store.data),
        userFeedback: feedback,
        comment: reason,
        summaryType,
        productSolutionId,
        rating,
      })
      blockStore.setFeedback(store)
    } catch (error) {
      store.error = "Unexpected error while sending feedback"
    } finally {
      store.isLoading = false
    }
    return store.error
  }

  voteForWorkbookReport = async (
    reportId: string,
    rating: number,
    comment: string
  ) => {
    const store = this.workbookReportsStore.getById(reportId).feedback

    try {
      store.isLoading = true
      store.rating = rating
      store.comment = comment
      store.isEditable = false

      await searchService.sendReportFeedback({
        reportId,
        comment,
        rating,
      })
    } catch (error) {
      store.error = "Unexpected error while sending feedback"
      store.rating = 3
      store.comment = ""
      store.isEditable = true
    } finally {
      store.isLoading = false
    }
    return store.error
  }

  updateAnswerUpVote = async (
    searchId: string,
    passage: AnswerItem,
    voted: boolean,
    answerType: AnswerViewType,
    avatarId: string
  ): Promise<string | null> => {
    const searchInstance = this.searchFlow.getById(searchId)
    const answer = passage
    try {
      if (!(searchInstance instanceof SearchEntityStore))
        throw new Error(
          "This answers data store does not support voting feature yet"
        )

      if (answer == null) throw new Error("passageId is expired")

      const newVote = voted ? 1 : 0
      const requestAnswer = { ...answer.value, current_user_vote: newVote }

      await searchService.upVoteAnswer(
        searchInstance.filter.searchQuery,
        requestAnswer,
        answerType,
        searchInstance.filter.searchAvatar,
        avatarId
      )

      // eslint-disable-next-line eqeqeq
      answer.value.current_user_vote = newVote
    } catch (error) {
      return `Failed to ${voted ? "upvote" : "downvote"} passage`
    }
    return null
  }

  getQueryHighlights = async (
    query: string,
    questionId: string
  ): Promise<boolean> => {
    const store = this.factFinderSolutionStore
    try {
      store.loadingHighlights = true

      const res = await searchService.getQueryHighlights(query, questionId)

      store.loadingHighlights = false

      if (res.status === 200) {
        const newHighlights = res.data

        // Append only new products
        const existingProductIds = new Set(
          store.highlights?.products.map((p) => p.id) || []
        )
        const newProducts = newHighlights.products.filter(
          (p) => !existingProductIds.has(p.id)
        )

        // Append only new companies
        const existingCompanyNames = new Set(
          store.highlights?.companies.map((c) => c.name) || []
        )
        const newCompanies = newHighlights.companies.filter(
          (c) => !existingCompanyNames.has(c.name)
        )

        // Update the store with new products and companies
        store.highlights = {
          products: [...(store.highlights?.products || []), ...newProducts],
          companies: [...(store.highlights?.companies || []), ...newCompanies],
        }

        return true
      }
    } catch (e) {
      return false
    }
    store.loadingHighlights = false
    return false
  }

  resetAutocomplete = () => {
    const store = this.factFinderSolutionStore.searchAutocomplete
    store.setData([])
  }

  autocompleteAbortController: AbortController | null = null

  loadAutocomplete = async (query: string): Promise<boolean> => {
    const store = this.factFinderSolutionStore.searchAutocomplete
    try {
      this.autocompleteAbortController?.abort()
      this.autocompleteAbortController = new AbortController()

      store.setLoading(true)

      const res = await searchService.getWorkbookSuggestionsByQuery(
        query,
        this.autocompleteAbortController.signal
      )

      store.setData(res.data.workflowSuggestions)
    } catch (e) {
      return false
    } finally {
      store.setLoading(false)
    }
    return true
  }

  loadWorkbookHighlights = async (query: string): Promise<boolean> => {
    const store = this.factFinderSolutionStore.workbookHighlights
    try {
      this.autocompleteAbortController?.abort()
      this.autocompleteAbortController = new AbortController()

      store.setLoading(true)

      if (!query) {
        store.setData([])
        return true
      }

      const res = await searchService.getWorkbookSuggestionsByQuery(
        query,
        this.autocompleteAbortController.signal
      )

      store.setData(res.data.workflowSuggestions)
    } catch (e) {
      return false
    } finally {
      store.setLoading(false)
    }
    return true
  }
}

export default FactFinderSolutionController
